import React from 'react';

/* ============ shared window chrome ============ */
export const WindowChrome = ({ label, children, accent = '#00E7FF', className = '', bodyClass = '' }) => (
  <div className={`glass rounded-xl overflow-hidden shadow-[0_24px_60px_rgba(0,0,0,0.55)] ${className}`} style={{ border: `1px solid ${accent}33` }}>
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[color:var(--itw-border)] bg-[color:var(--itw-surface-2)]/70">
      <span className="h-2.5 w-2.5 rounded-full bg-[#FF5C7A]" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#FFCC66]" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#2EF2A5]" />
      <span className="ml-3 font-mono text-[11px] text-[color:var(--itw-text-3)] truncate">{label}</span>
    </div>
    <div className={`p-4 ${bodyClass}`}>{children}</div>
  </div>
);

const Bars = ({ accent, n = 24, heights }) => (
  <div className="flex items-end gap-[3px] h-16">
    {Array.from({ length: n }).map((_, i) => {
      const h = heights ? heights[i % heights.length] : 20 + ((i * 37) % 80);
      return <div key={i} className="flex-1 rounded-sm origin-bottom" style={{ height: `${h}%`, background: `linear-gradient(180deg, ${accent}, ${accent}22)`, animation: `itw-bar 0.9s ease-out ${i * 0.03}s both` }} />;
    })}
  </div>
);

const AreaChart = ({ accent }) => (
  <svg viewBox="0 0 320 90" className="w-full h-24" preserveAspectRatio="none">
    <defs>
      <linearGradient id="ac" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={accent} stopOpacity="0.35" />
        <stop offset="100%" stopColor={accent} stopOpacity="0" />
      </linearGradient>
    </defs>
    <path d="M0,70 L30,60 L60,64 L90,42 L120,50 L150,30 L180,38 L210,20 L240,28 L270,14 L300,22 L320,16 L320,90 L0,90 Z" fill="url(#ac)" />
    <path className="dash-draw" d="M0,70 L30,60 L60,64 L90,42 L120,50 L150,30 L180,38 L210,20 L240,28 L270,14 L300,22 L320,16" fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Metric = ({ label, value, accent, sub }) => (
  <div className="rounded-lg bg-[color:var(--itw-surface-2)] border border-[color:var(--itw-border)] p-3">
    <div className="text-[10px] uppercase tracking-wide text-[color:var(--itw-text-3)]">{label}</div>
    <div className="mt-1 font-mono text-lg font-semibold" style={{ color: accent }}>{value}</div>
    {sub && <div className="text-[10px] text-[color:var(--itw-text-3)] mt-0.5">{sub}</div>}
  </div>
);

/* ============ Hosting / Server: monitoring dashboard ============ */
export const DashboardMock = ({ accent = '#00E7FF' }) => (
  <WindowChrome label="console.it-welt360.net /monitoring" accent={accent}>
    <div className="grid grid-cols-4 gap-2.5">
      <Metric label="Uptime" value="99.99%" accent="#2EF2A5" sub="30d" />
      <Metric label="CPU" value="34%" accent={accent} sub="8 vCores" />
      <Metric label="RAM" value="61%" accent={accent} sub="32 GB" />
      <Metric label="Req/s" value="1.2k" accent={accent} sub="+18%" />
    </div>
    <div className="mt-3 rounded-lg bg-[color:var(--itw-surface-2)] border border-[color:var(--itw-border)] p-3">
      <div className="flex items-center justify-between text-[11px] text-[color:var(--itw-text-3)] mb-1">
        <span className="font-mono">Traffic (24h)</span><span className="font-mono" style={{ color: accent }}>2.4 TB</span>
      </div>
      <AreaChart accent={accent} />
    </div>
    <div className="mt-3 space-y-1.5">
      {[['web-01', 'operational'], ['db-01', 'operational'], ['cache-01', 'operational']].map(([n, s]) => (
        <div key={n} className="flex items-center justify-between text-[12px] font-mono">
          <span className="text-[color:var(--itw-text-2)]">{n}.it-welt360.net</span>
          <span className="flex items-center gap-1.5 text-[#2EF2A5]"><span className="h-1.5 w-1.5 rounded-full bg-[#2EF2A5] animate-blink" />{s}</span>
        </div>
      ))}
    </div>
  </WindowChrome>
);

export const ServerMetrics = ({ accent = '#00E7FF' }) => (
  <WindowChrome label="infra.it-welt360.net /cluster" accent={accent}>
    <div className="grid grid-cols-3 gap-2.5">
      {[['Nodes', '12'], ['Load', '0.42'], ['Failover', 'Ready']].map(([l, v]) => <Metric key={l} label={l} value={v} accent={accent} />)}
    </div>
    <div className="mt-3 space-y-2">
      {[['node-a1', 42], ['node-a2', 66], ['node-b1', 28], ['node-b2', 51]].map(([n, p]) => (
        <div key={n} className="text-[12px] font-mono">
          <div className="flex justify-between text-[color:var(--itw-text-2)]"><span>{n}</span><span style={{ color: accent }}>{p}%</span></div>
          <div className="mt-1 h-1.5 rounded-full bg-[color:var(--itw-surface-2)] overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${p}%`, background: accent }} />
          </div>
        </div>
      ))}
    </div>
    <div className="mt-3"><Bars accent={accent} n={20} /></div>
  </WindowChrome>
);

/* ============ Netzwerk: topology map ============ */
export const NetworkMap = ({ accent = '#2EF2A5' }) => {
  const P = {
    net: 'M230,34 L230,86', fw: 'M230,110 L230,160', c1: 'M226,180 L120,232', c2: 'M230,184 L230,232', c3: 'M234,180 L342,232',
    vpn: 'M254,100 L372,118', ep: 'M206,100 L92,118',
  };
  const Node = ({ x, y, label, w = 92 }) => (
    <g>
      <rect x={x - w / 2} y={y - 15} width={w} height={30} rx={7} fill="#0F1621" stroke={accent} strokeOpacity="0.5" />
      <circle cx={x - w / 2 + 12} cy={y} r={3.5} fill={accent} />
      <text x={x + 6} y={y + 4} textAnchor="middle" fontSize="10" fill="#A9B6CC" fontFamily="IBM Plex Mono, monospace">{label}</text>
    </g>
  );
  const Packet = ({ path, dur }) => (
    <circle r="3.2" fill={accent}>
      <animateMotion dur={dur} repeatCount="indefinite" path={path} />
    </circle>
  );
  return (
    <WindowChrome label="net.it-welt360.net /topology · zero-trust" accent={accent}>
      <svg viewBox="0 0 460 300" className="w-full h-[300px]">
        {Object.values(P).map((d, i) => <path key={i} d={d} stroke={accent} strokeOpacity="0.3" strokeWidth="1.5" fill="none" />)}
        <Packet path={P.net} dur="1.8s" /><Packet path={P.fw} dur="1.6s" /><Packet path={P.c2} dur="2s" /><Packet path={P.c1} dur="2.4s" /><Packet path={P.vpn} dur="2.2s" />
        <Node x={230} y={30} label="Internet" />
        <Node x={230} y={100} label="Firewall/WAF" w={110} />
        <Node x={230} y={172} label="Core Switch" w={100} />
        <Node x={110} y={248} label="Web" w={70} />
        <Node x={230} y={248} label="DB" w={70} />
        <Node x={350} y={248} label="Mail" w={70} />
        <Node x={392} y={118} label="VPN" w={64} />
        <Node x={70} y={118} label="Clients" w={80} />
      </svg>
      <div className="flex flex-wrap gap-2 mt-1">
        {['Zero-Trust', 'IDS aktiv', 'AES-256', '0 Threats'].map((b) => (
          <span key={b} className="font-mono text-[10px] px-2 py-1 rounded-md" style={{ color: accent, background: `${accent}14`, border: `1px solid ${accent}33` }}>{b}</span>
        ))}
      </div>
    </WindowChrome>
  );
};

/* ============ Softwareentwicklung: code editor ============ */
export const CodeEditor = ({ accent = '#5AF3FF' }) => {
  const line = (num, content) => (
    <div className="flex" key={num}>
      <span className="w-8 shrink-0 text-right pr-3 text-[color:var(--itw-text-3)] select-none">{num}</span>
      <span className="whitespace-pre">{content}</span>
    </div>
  );
  const kw = (t) => <span style={{ color: '#FF7EB6' }}>{t}</span>;
  const fn = (t) => <span style={{ color: accent }}>{t}</span>;
  const str = (t) => <span style={{ color: '#2EF2A5' }}>{t}</span>;
  const cm = (t) => <span style={{ color: '#7E8AA3' }}>{t}</span>;
  return (
    <WindowChrome label="~/it-welt360/api/service.py" accent={accent} bodyClass="p-0">
      <div className="flex border-b border-[color:var(--itw-border)] text-[11px] font-mono">
        <span className="px-3 py-2 text-[color:var(--itw-text)] border-b-2" style={{ borderColor: accent }}>service.py</span>
        <span className="px-3 py-2 text-[color:var(--itw-text-3)]">deploy.yml</span>
        <span className="px-3 py-2 text-[color:var(--itw-text-3)]">Dockerfile</span>
      </div>
      <div className="p-4 font-mono text-[12px] leading-6 text-[color:var(--itw-text-2)]">
        {line(1, <>{cm('# IT-WELT360 · custom API')}</>)}
        {line(2, <>{kw('async def')} {fn('deploy')}(env: {kw('str')}):</>)}
        {line(3, <>{'  '}service = {fn('Cluster')}(scale={str('"auto"')})</>)}
        {line(4, <>{'  '}{kw('await')} service.{fn('rollout')}(env)</>)}
        {line(5, <>{'  '}{kw('return')} {str('"live"')}</>)}
        {line(6, <> </>)}
        {line(7, <>{cm('# ci: build + test + ship')}<span className="inline-block w-2 h-4 -mb-0.5 ml-0.5 animate-blink" style={{ background: accent }} /></>)}
      </div>
      <div className="flex items-center justify-between px-4 py-2 border-t border-[color:var(--itw-border)] text-[11px] font-mono">
        <span className="text-[#2EF2A5]">● build passed · 0 errors</span>
        <span className="text-[color:var(--itw-text-3)]">main ↑ 12 tests ✓</span>
      </div>
    </WindowChrome>
  );
};

/* ============ Sicherheit: CCTV grid ============ */
export const CctvGrid = ({ accent = '#5AF3FF' }) => (
  <WindowChrome label="secure.it-welt360.net /surveillance" accent={accent} bodyClass="p-3">
    <div className="grid grid-cols-2 gap-2">
      {['CAM 01 · Eingang', 'CAM 02 · Lager', 'CAM 03 · Kasse', 'CAM 04 · Parkplatz'].map((c, i) => (
        <div key={c} className="relative aspect-video rounded-md overflow-hidden border border-[color:var(--itw-border)]" style={{ background: 'linear-gradient(135deg,#0b1017,#0f1621)' }}>
          <div className="absolute inset-0 opacity-40" style={{ backgroundImage: 'linear-gradient(rgba(90,243,255,0.08) 1px,transparent 1px)', backgroundSize: '100% 8px' }} />
          <div className="absolute top-1.5 left-2 font-mono text-[9px] text-[color:var(--itw-text-2)]">{c}</div>
          <div className="absolute top-1.5 right-2 flex items-center gap-1 font-mono text-[9px] text-[#FF5C7A]"><span className="h-1.5 w-1.5 rounded-full bg-[#FF5C7A] animate-blink" />REC</div>
          <div className="absolute inset-y-0 w-1/3 opacity-30" style={{ background: `linear-gradient(90deg,transparent, ${accent}, transparent)`, animation: `itw-sweep ${3 + i * 0.5}s linear infinite` }} />
          <div className="absolute bottom-1.5 left-2 font-mono text-[8px] text-[color:var(--itw-text-3)]">23:0{i}:14 · 1080p</div>
        </div>
      ))}
    </div>
    <div className="mt-3 flex items-center justify-between rounded-lg bg-[color:var(--itw-surface-2)] border border-[color:var(--itw-border)] px-3 py-2 font-mono text-[11px]">
      <span className="flex items-center gap-1.5" style={{ color: accent }}><span className="h-1.5 w-1.5 rounded-full animate-blink" style={{ background: accent }} />SYSTEM ARMED</span>
      <span className="text-[color:var(--itw-text-3)]">4 Kameras online · 0 Alarme</span>
    </div>
  </WindowChrome>
);

/* ============ Kassensysteme: POS checkout ============ */
export const PosCheckout = ({ accent = '#00E7FF' }) => (
  <WindowChrome label="pos.it-welt360.net · Kasse 01" accent={accent}>
    <div className="grid grid-cols-5 gap-3">
      <div className="col-span-3 space-y-1.5">
        {[['Espresso', '2,80'], ['Croissant', '3,20'], ['Menu 1', '9,90'], ['Wasser 0,5l', '2,50']].map(([n, p]) => (
          <div key={n} className="flex justify-between text-[12px] font-mono text-[color:var(--itw-text-2)] border-b border-[color:var(--itw-border)] pb-1">
            <span>{n}</span><span>€ {p}</span>
          </div>
        ))}
        <div className="flex justify-between text-[13px] font-mono pt-1"><span className="text-[color:var(--itw-text-3)]">MwSt 19%</span><span className="text-[color:var(--itw-text-3)]">€ 3,10</span></div>
        <div className="flex justify-between font-mono"><span className="text-[color:var(--itw-text)]">Summe</span><span className="text-lg font-semibold" style={{ color: accent }}>€ 18,40</span></div>
      </div>
      <div className="col-span-2 space-y-2">
        <button className="w-full py-2.5 rounded-lg font-medium text-[13px] text-[color:var(--itw-bg)]" style={{ background: accent }}>Karte</button>
        <button className="w-full py-2.5 rounded-lg font-medium text-[13px] text-[color:var(--itw-text)] border border-[color:var(--itw-border)]">Bar</button>
        <div className="grid grid-cols-3 gap-1.5 pt-1">
          {[1,2,3,4,5,6,7,8,9].map((k) => <div key={k} className="aspect-square grid place-items-center rounded-md bg-[color:var(--itw-surface-2)] border border-[color:var(--itw-border)] font-mono text-[12px] text-[color:var(--itw-text-2)]">{k}</div>)}
        </div>
      </div>
    </div>
    <div className="mt-3 flex gap-2">
      {['TSE ✓', 'GoBD-konform', 'EC + Kreditkarte'].map((b) => <span key={b} className="font-mono text-[10px] px-2 py-1 rounded-md" style={{ color: accent, background: `${accent}14`, border: `1px solid ${accent}33` }}>{b}</span>)}
    </div>
  </WindowChrome>
);

/* ============ JTL: shop analytics ============ */
export const ShopAnalytics = ({ accent = '#5AF3FF' }) => (
  <WindowChrome label="shop.it-welt360.net /analytics · JTL" accent={accent}>
    <div className="grid grid-cols-3 gap-2.5">
      <Metric label="Umsatz heute" value="€ 12.480" accent="#2EF2A5" sub="+18%" />
      <Metric label="Bestellungen" value="214" accent={accent} sub="+31" />
      <Metric label="Warenkorb Ø" value="€ 58" accent={accent} />
    </div>
    <div className="mt-3 rounded-lg bg-[color:var(--itw-surface-2)] border border-[color:var(--itw-border)] p-3">
      <div className="flex items-center justify-between text-[11px] font-mono text-[color:var(--itw-text-3)] mb-2"><span>Umsatz / Woche</span><span style={{ color: accent }}>€ 68.2k</span></div>
      <Bars accent={accent} n={7} heights={[45, 62, 40, 78, 55, 90, 70]} />
      <div className="flex justify-between mt-1 font-mono text-[9px] text-[color:var(--itw-text-3)]"><span>Mo</span><span>Di</span><span>Mi</span><span>Do</span><span>Fr</span><span>Sa</span><span>So</span></div>
    </div>
    <div className="mt-3 space-y-1.5">
      {[['SSD 1TB NVMe', '48×'], ['Mini-PC i5', '31×'], ['USB-C Hub', '27×']].map(([n, q]) => (
        <div key={n} className="flex justify-between text-[12px] font-mono"><span className="text-[color:var(--itw-text-2)]">{n}</span><span style={{ color: accent }}>{q}</span></div>
      ))}
    </div>
  </WindowChrome>
);

/* ============ Support: ticket console ============ */
export const TicketConsole = ({ accent = '#00E7FF' }) => {
  const rows = [
    ['#4821', 'Mailserver langsam', 'Hoch', '#FF5C7A', '00:08'],
    ['#4820', 'VPN Zugang einrichten', 'Mittel', '#FFCC66', '01:12'],
    ['#4817', 'Backup wiederherstellen', 'Hoch', '#FF5C7A', '00:03'],
    ['#4814', 'Drucker im Netzwerk', 'Niedrig', '#2EF2A5', '03:40'],
  ];
  return (
    <WindowChrome label="support.it-welt360.net /helpdesk" accent={accent}>
      <div className="grid grid-cols-3 gap-2.5 mb-3">
        <Metric label="Ø Reaktion" value="12 min" accent="#2EF2A5" />
        <Metric label="SLA" value="99.8%" accent={accent} />
        <Metric label="Offen" value="7" accent={accent} />
      </div>
      <div className="space-y-1.5">
        {rows.map(([id, subj, prio, col, sla]) => (
          <div key={id} className="flex items-center gap-2 rounded-lg bg-[color:var(--itw-surface-2)] border border-[color:var(--itw-border)] px-3 py-2 text-[12px] font-mono">
            <span className="text-[color:var(--itw-text-3)] w-12">{id}</span>
            <span className="flex-1 text-[color:var(--itw-text-2)] truncate">{subj}</span>
            <span className="px-2 py-0.5 rounded text-[10px]" style={{ color: col, background: `${col}18` }}>{prio}</span>
            <span className="text-[color:var(--itw-text-3)] w-12 text-right">SLA {sla}</span>
          </div>
        ))}
      </div>
    </WindowChrome>
  );
};

/* ============ Logo / tech marquee ============ */
export const TechMarquee = () => {
  const items = ['JTL-Wawi', 'Docker', 'Kubernetes', 'PostgreSQL', 'Linux', 'Cloudflare', 'WireGuard', 'Redis', 'Microsoft 365', 'Proxmox', 'Nginx', 'Grafana'];
  const row = [...items, ...items];
  return (
    <div className="relative overflow-hidden py-2 [mask-image:linear-gradient(90deg,transparent,#000_12%,#000_88%,transparent)]">
      <div className="flex gap-10 w-max marquee-track">
        {row.map((t, i) => (
          <span key={i} className="font-mono text-sm text-[color:var(--itw-text-3)] whitespace-nowrap">{t}</span>
        ))}
      </div>
    </div>
  );
};

/* ============ Geräte-Reparatur: repair bench console ============ */
export const RepairConsole = ({ accent = '#5AF3FF' }) => {
  const jobs = [
    ['#R-2481', 'MacBook Pro · Display', 'Diagnose', '#FFCC66'],
    ['#R-2480', 'ThinkPad · SSD-Upgrade', 'Reparatur', accent],
    ['#R-2478', 'iPhone 14 · Akku', 'Qualitätscheck', '#2EF2A5'],
    ['#R-2475', 'HDD · Datenrettung', 'Bereit', '#2EF2A5'],
  ];
  return (
    <WindowChrome label="werkstatt.it-welt360.net /reparatur-queue" accent={accent}>
      <div className="grid grid-cols-3 gap-2.5">
        <Metric label="In Werkstatt" value="14" accent={accent} sub="heute" />
        <Metric label="Ø Bearbeitung" value="1,4 Tg" accent="#2EF2A5" />
        <Metric label="Erfolgsquote" value="98%" accent="#2EF2A5" sub="Datenrettung" />
      </div>
      <div className="mt-3 space-y-1.5">
        {jobs.map(([id, subj, phase, col]) => (
          <div key={id} className="flex items-center gap-2 rounded-lg bg-[color:var(--itw-surface-2)] border border-[color:var(--itw-border)] px-3 py-2 text-[12px] font-mono">
            <span className="text-[color:var(--itw-text-3)] w-16">{id}</span>
            <span className="flex-1 text-[color:var(--itw-text-2)] truncate">{subj}</span>
            <span className="px-2 py-0.5 rounded text-[10px]" style={{ color: col, background: `${col}18` }}>{phase}</span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {['Festpreis', 'DSGVO', 'Garantie', 'Express'].map((b) => (
          <span key={b} className="font-mono text-[10px] px-2 py-1 rounded-md" style={{ color: accent, background: `${accent}14`, border: `1px solid ${accent}33` }}>{b}</span>
        ))}
      </div>
    </WindowChrome>
  );
};

/* ============ resolver ============ */
const MAP = {
  hosting: DashboardMock,
  server: ServerMetrics,
  netzwerk: NetworkMap,
  softwareentwicklung: CodeEditor,
  sicherheit: CctvGrid,
  kassensysteme: PosCheckout,
  jtl: ShopAnalytics,
  support: TicketConsole,
  'geraete-reparatur': RepairConsole,
};

export const ServiceVisual = ({ slug, accent }) => {
  const Cmp = MAP[slug] || DashboardMock;
  return <Cmp accent={accent} />;
};

// secondary visual (different from primary) for deep-dive sections
const MAP2 = {
  hosting: ServerMetrics,
  server: DashboardMock,
  netzwerk: CodeEditor,
  softwareentwicklung: TerminalLike,
  sicherheit: NetworkMap,
  kassensysteme: ShopAnalytics,
  jtl: DashboardMock,
  support: NetworkMap,
};
function TerminalLike({ accent = '#5AF3FF' }) {
  return (
    <WindowChrome label="root@it-welt360:~ · ci/cd" accent={accent}>
      <div className="font-mono text-[12px] leading-6">
        <div className="text-[color:var(--itw-text)]">$ git push origin main</div>
        <div className="text-[color:var(--itw-text-3)]">&gt;&gt; running pipeline ...</div>
        <div className="text-[#2EF2A5]">[OK] lint · test · build</div>
        <div className="text-[#2EF2A5]">[OK] deploy 12/12 pods</div>
        <div className="text-[color:var(--itw-text)]">$ <span className="inline-block w-2 h-4 -mb-0.5 animate-blink" style={{ background: accent }} /></div>
      </div>
    </WindowChrome>
  );
}
export const ServiceVisualSecondary = ({ slug, accent }) => {
  const Cmp = MAP2[slug] || TerminalLike;
  return <Cmp accent={accent} />;
};
